import Query from "@arcgis/core/rest/support/Query";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import usePullToRefresh from "../../../hooks/usePoolToRefresh";
import useWindowSize from "../../../hooks/useWindowSize";
import { ConfigContext } from "../../../utils/ConfigContext";
import { getOpsColor } from "../../../utils/helper";

import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import {
  asOfDateFieldName,
  currAsOfDateFieldName,
  showpublicFieldName,
} from "../../../utils/API";
import FeatureVirtualList from "./FeatureVirtualList";
import { LatestUpdatesWrapper } from "./LatestUpdates-styled";

export const LatestUpdatesIcon = ({ color, expanded, ...props }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill={color ?? "currentColor"}
      {...props}
    >
      <path d="M1 2v2.582A7.795 7.795 0 1 1 .272 9h1.01a6.802 6.802 0 1 0 .625-4H4v1H0V2zm10 7H8V5H7v5h4z" />
    </svg>
  );
};

const LatestUpdates = ({ expanded, searchText = "" }) => {
  const [selectedId, setSelectedId] = useState(null);

  const activeModule = useSelector((state) => state.activeModule);
  const mobileReducer = useSelector((state) => state.mobileReducer);
  const view = useSelector((state) => state.mapViewReducer.view);

  const { config } = useContext(ConfigContext);
  const { t } = useTranslation("common");

  const { width } = useWindowSize();

  const isMobile = useMemo(() => {
    return width <= 768 || mobileReducer.isMobileApp;
  }, [width, mobileReducer.isMobileApp]);
  /**
   * This is only a trigger to re-render the page. every time we load new data (feature or attachment)
   * we change the values of this trigger
   */
  let [layersLoaded, setLayersLoaded] = useState(0);
  const [loading, setLoading] = useState(false);
  const [extent, setExtent] = useState(undefined);
  const extentListener = useRef();
  const extentRef = useRef(extent);
  const watchHandlers = useRef([]);

  const refreshRef = useRef();
  const loaderRef = useRef();
  usePullToRefresh(
    refreshRef,
    () => {
      view.map.layers.forEach((layer) => {
        processLayer(layer);
      });
    },
    loaderRef
  );

  const isShownInModule = useMemo(() => {
    if (!config) return false;
    const module =
      config.modules[activeModule] ?? config.opDefaults.modules[activeModule];
    return module?.isLatestUpdatesShown ?? true;
  }, [activeModule]);

  useEffect(() => {
    if (!isShownInModule) {
      watchHandlers.current.forEach((h) => h.remove());
      watchHandlers.current = [];
      return;
    }

    const layersChangeHandler = view.map.layers.on("change", (event) => {
      if (event.added)
        event.added.forEach((layer) => {
          processLayer(layer);
          const editHandler = layer.on("edits", (event) => {
            processLayer(layer);
          });
          const refreshHandler = layer.on("refresh", () => {
            processLayer(layer);
          });
          const visibleHandler = reactiveUtils.watch(
            () => layer.visible,
            () => {
              setLayersLoaded(layersLoaded++);
            }
          );
          watchHandlers.current.push(
            editHandler,
            refreshHandler,
            visibleHandler
          );
        });
    });
    watchHandlers.current.push(layersChangeHandler);

    view.map.layers.forEach((layer) => {
      processLayer(layer);

      //Update page when edit or refresh events occur:
      const editHandler = layer.on("edits", (event) => {
        processLayer(layer);
      });
      const refreshHandler = layer.on("refresh", () => {
        processLayer(layer);
      });
      const visibleHandler = reactiveUtils.watch(
        () => layer.visible,
        () => {
          setLayersLoaded(layersLoaded++);
        }
      );
      watchHandlers.current.push(editHandler, refreshHandler, visibleHandler);
    });

    return () => {
      watchHandlers.current.forEach((h) => h.remove());
    };
  }, [isShownInModule]);

  const processLayer = (layer) => {
    layer.load().then((layer) => {
      if (
        layer.type !== "feature" ||
        !layer.layerConfig ||
        !layer.layerConfig?.isShownInLatestUpdates ||
        !layer.editFieldsInfo?.editDateField
      ) {
        layer.lastEditedFeaturesQueryReady = true;
        setLayersLoaded(layersLoaded++);
        // disableExtentLoading();
        return;
      }

      const editDateFieldName = layer.editFieldsInfo?.editDateField;
      const editDateField = layer.fields.filter(
        (field) => field.name === editDateFieldName
      )[0];

      if (!editDateField) {
        console.warn(
          "Layer: " +
            layer.title +
            " defines a missing editDateField: " +
            editDateFieldName
        );
        layer.lastEditedFeaturesQueryReady = true;
        setLayersLoaded(layersLoaded++);
        // disableExtentLoading();
        return;
      }

      queryLayer(layer, editDateFieldName);
      reactiveUtils.watch(
        () => layer.definitionExpression,
        () => {
          queryLayer(layer, editDateFieldName);
        }
      );
    });
  };

  /**
   * Query a layer for the 5 most (or least) recently edited features and their attachments
   * the results will be saved in the layer.lastEditedFeatures property
   */
  const queryLayer = (layer, editDateFieldName) => {
    const query = new Query();
    query.outFields = ["*"];
    query.where = editDateFieldName + " is not null";
    const hasCurrStatus = layer.fields.some(
      (field) => field.name === "currstatus_operational"
    );

    if (layer.definitionExpression)
      query.where += " and (" + layer.definitionExpression + ")";

    if (hasCurrStatus) {
      query.where += " and currstatus_operational is not null";
    }

    query.orderByFields = [editDateFieldName + " DESC"];
    query.returnGeometry = true;
    if (extentRef.current) {
      query.geometry = view.extent;
    }

    layer
      .queryFeatures(query)
      .then((lastEditedFeatures) => {
        const lastEditedFiltered = lastEditedFeatures.features.filter(
          (ftr) =>
            ftr.attributes[editDateFieldName] &&
            ftr.attributes[editDateFieldName] != null
        );

        if (
          isEqual(
            layer.lastEditedFeatures,
            lastEditedFiltered,
            layer,
            editDateFieldName
          )
        ) {
          layer.lastEditedFeaturesQueryReady = true;
          // disableExtentLoading();
          return;
        }

        layer.lastEditedFeatures = lastEditedFiltered;
        if (
          !layer.lastEditedFeatures ||
          layer.lastEditedFeatures.length === 0 ||
          !layer.capabilities.operations.supportsQueryAttachments
        ) {
          layer.lastEditedFeaturesQueryReady = true;
          setLayersLoaded(layersLoaded++);
          // disableExtentLoading();
          return;
        }

        setLayersLoaded(layersLoaded++);
        queryLayerAttachments(layer);
        // disableExtentLoading();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const isEqual = (resA, resB, layer, editDateFieldName) => {
    const lenA = !resA ? 0 : resA.length;
    const lenB = !resB ? 0 : resB.length;
    if (lenA !== lenB) return false;

    const oidField = layer.objectIdField;
    for (let idx = 0; idx < lenA; idx++) {
      const fa = resA[idx];
      const fb = resB[idx];
      if (
        fa.attributes[editDateFieldName] !== fb.attributes[editDateFieldName] ||
        fa.attributes[oidField] !== fb.attributes[oidField]
      ) {
        return false;
      }
    }

    return true;
  };
  /**
   * Query the layer for attachments of the most (or least) recently edited features.
   * The features to query for are already stored in the layer.lastEditedFeatures property
   */
  const queryLayerAttachments = (layer) => {
    const oidField = layer.objectIdField;
    const objectIds = layer.lastEditedFeatures.map(
      (feature) => feature.attributes[oidField]
    );

    layer
      .queryAttachments({ objectIds: objectIds })
      .then((response) => {
        response &&
          Object.keys(response).forEach((oid) => {
            // Each object might have different attachments
            const objectAtts = response[oid];

            layer.lastEditedFeatures.forEach((feature) => {
              // Save attachments into the layer features array, assigned to the correct feature
              if (feature.attributes[oidField].toString() === oid) {
                feature.attachments = objectAtts;
              }
            });
          });

        layer.lastEditedFeaturesQueryReady = true;
        setLayersLoaded(layersLoaded++);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const getFeatureEditDate = (feature) => {
    const editDateFieldName = feature.layer.editFieldsInfo?.editDateField;
    const field = [
      currAsOfDateFieldName,
      asOfDateFieldName,
      editDateFieldName,
    ].find((f) => f && feature.attributes.hasOwnProperty(f));

    if (!field) return;

    return feature.attributes[field];
  };

  const compareFeatures = (a, b) => {
    const editDateA = getFeatureEditDate(a);
    const editDateB = getFeatureEditDate(b);

    // If both are missing, consider them equal
    if (!editDateA && !editDateB) return 0;
    // If only A is missing, B comes first
    if (!editDateA) return 1;
    // If only B is missing, A comes first
    if (!editDateB) return -1;

    // Descending order: latest date first
    return editDateB - editDateA;
  };

  const [featuresToShow, setFeaturesToShow] = useState([]);

  useEffect(() => {
    let timer;
    let features = [];
    setLoading(true);
    timer = setTimeout(() => {
      setLoading(false);

      view.map.layers.forEach((layer) => {
        if (layer.visible && layer.lastEditedFeatures && isShownInModule) {
          const featuresShownOnUpdate = layer.lastEditedFeatures.filter(
            (feat) =>
              (feat.attributes[showpublicFieldName] === undefined ||
                feat.attributes[showpublicFieldName] === 1) &&
              !(
                getFeatureEditDate(feat) === null ||
                getFeatureEditDate(feat) === undefined
              )
          );
          features.push(...featuresShownOnUpdate);
        }
      });

      features.sort((a, b) => compareFeatures(a, b));

      setFeaturesToShow(features);
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [layersLoaded, isShownInModule]);

  const filteredFeatures = useMemo(() => {
    if (!expanded) return [];

    if (!searchText || searchText.length === 0) {
      return featuresToShow;
    }

    return featuresToShow.filter((feat) => {
      const layer = feat.sourceLayer ?? feat.layer;
      if (layer) {
        const titleTemplate = layer?.layerConfig?.titleTemplate;
        const searchField = titleTemplate
          ?.substring(1, titleTemplate.length - 1)
          ?.replace("feature.", "");

        if (
          searchField &&
          feat.attributes[searchField] &&
          typeof feat.attributes[searchField] === "string"
        ) {
          const lowerValue = feat.attributes[searchField].toLowerCase();
          const lowerText = searchText.toLowerCase();
          return lowerValue.includes(lowerText);
        }

        return false;
      }
      return false;
    });
  }, [featuresToShow, searchText, expanded]);

  // useEffect(() => {
  //   if (searchText.length > 0) {
  //     featuresToShow = featuresToShow.filter((feat) => {
  //       const layer = feat.sourceLayer ?? feat.layer;
  //       if (layer) {
  //         const titleTemplate = layer?.layerConfig?.titleTemplate;
  //         const searchField = titleTemplate
  //           ?.substring(1, titleTemplate.length - 1)
  //           ?.replace("feature.", "");

  //         if (
  //           searchField &&
  //           feat.attributes[searchField] &&
  //           typeof feat.attributes[searchField] === "string"
  //         ) {
  //           const lowerValue = feat.attributes[searchField].toLowerCase();
  //           const lowerText = searchText.toLowerCase();
  //           return lowerValue.includes(lowerText);
  //         }

  //         return false;
  //       }
  //       return false;
  //     });
  //   }

  //   return () => {};
  // }, [searchText, featuresToShow]);

  const mounted = useRef(false);

  useEffect(() => {
    if (featuresToShow.length > 0 && !mounted.current) {
      mounted.current = true;
    }

    if (featuresToShow.length === 0 && mounted.current) {
      mounted.current = false;
    }
  }, [featuresToShow]);

  useEffect(() => {
    let watchHandler, timer, selectedFeatureHandler;
    watchHandler = reactiveUtils.watch(
      () => view.popup.visible,
      (visible) => {
        if (visible) {
          selectedFeatureHandler?.remove();
          const popupFeature = view.popup.selectedFeature;
          if (popupFeature) {
            setSelectedId(popupFeature.getObjectId());
          }

          selectedFeatureHandler = reactiveUtils.watch(
            () => view.popup.selectedFeature,
            (graphic) => {
              if (graphic) {
                setSelectedId(graphic.getObjectId());
              } else {
                setSelectedId(null);
              }
            }
          );
        } else {
          selectedFeatureHandler?.remove();
          setSelectedId(null);
        }
      },
      { initial: true }
    );

    return () => {
      watchHandler?.remove();
      selectedFeatureHandler?.remove();
      clearTimeout(timer);
    };
  }, [view]);

  const opsColor = useMemo(() => getOpsColor(config), [config, activeModule]);

  return (
    <LatestUpdatesWrapper
      className={
        !isMobile && featuresToShow.length > 0
          ? "onboarding-latest-updates"
          : ""
      }
      style={{
        overflow: "hidden",
      }}
    >
      {!isMobile && (
        <div style={{ paddingBottom: 10, textAlign: "center" }}>
          <span
            style={{
              fontSize: "17px",
              color: "var(--fontColor)",
              display: "inline-block",
              textWrap: "nowrap",
              lineHeight: "normal",
            }}
          >
            {t("screen.widget.LatestUpdates.title", "")}
          </span>
        </div>
      )}
      <div
        ref={loaderRef}
        id="refresh"
        style={{
          opacity: 0,
          position: "absolute",
          top: isMobile ? 4 : 28,
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        <calcite-loader scale="s"></calcite-loader>
      </div>
      <div
        style={{
          overflow: "hidden",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: isMobile ? "0px 0px 12px 0px" : "0px 8px 12px 8px",

          background: isMobile ? "#eee" : undefined,
        }}
      >
        <FeatureVirtualList
          ref={refreshRef}
          features={filteredFeatures}
          style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}
          selectedId={selectedId}
        >
          <div
            style={{
              fontSize: "small",
              cursor: "pointer",
              padding: "4px 0px",
              textAlign: "center",
            }}
          >
            {loading && <calcite-loader scale="s"></calcite-loader>}
            {!loading && t("screen.message.noMoreInformation")}
          </div>
        </FeatureVirtualList>
      </div>
    </LatestUpdatesWrapper>
  );
};

export default LatestUpdates;
